import { upperCamelCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useMemo } from 'react';

import { ApiOrgContractor } from '@kaiizen/kai-pro-api';
import { useAllOrgSettings } from '@kaiizen/kai-pro-api-react-state';
import { contractorHasChanged, ScanRootOptimized, StandardTaskTrade } from '@kaiizen/kai-scan-template-system';

import { contractorCanPerformTrade } from '@/Client/Lib/contractorTradeLib';
import { COLORS2, LAYOUT2 } from '@/Client/Styles';
import { useScanEditingContextSlice } from '../../State';
import { LabeledInputSize, LabeledSelect } from '../../Widgets';

const isGeneralContractor = (trades?: string[]) => !!trades && trades.includes('general');
const contractorSort = (a: ApiOrgContractor, b: ApiOrgContractor) =>
  isGeneralContractor(a.trades) ? -1 : isGeneralContractor(b.trades)
    ? 1
    : a.name.localeCompare(b.name);

const tradePlaceholderMap: Record<StandardTaskTrade, string> = {
  general: 'General Contractor',
  carpenter: 'Carpenter',
  electrician: 'Electrician',
  plumber: 'Plumber',
  hvac: 'HVAC Contractor',
  roofer: 'Roofer',
  mason: 'Mason',
  flooring: 'Flooring Contractor',
  tiler: 'Tiler',
  painting: 'Painter',
  insulation: 'Insulation',
  glazier: 'Glazier',
  windows: 'Windows Contractor',
  countertops: 'Countertops Contractor',
  cabinets: 'Cabinets Contractor',
  concrete: 'Concrete Contractor',
  steel: 'Steel Contractor',
  excavation: 'Excavation Contractor',
  landscaper: 'Landscaper',
  pestControl: 'Pest Control Contractor',
  septic: 'Septic Contractor',
  structuralEngineer: 'Structural Engineer',
  cleaner: 'Cleaner',
  garageDoor: 'Garage Door Contractor',
  poolVendor: 'Pool Vendor',
  inHouse: 'In-House',
  appliances: 'Appliances Contractor',
  foundation: 'Foundation',
  locksmithRekey: 'Locksmith / Rekey',
  resurfacing: 'Resurfacing',
};

const CONTRACTOR_PICKER_STYLE = css`
  label: CONTRACTOR_PICKER_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  width: 100%;
`;

const getTradeDisplayName = (
  trade: string,
  configuredTrades?: Record<string, { displayName?: string }>,
) =>
  configuredTrades?.[trade]?.displayName
  || tradePlaceholderMap[trade as StandardTaskTrade]
  || upperCamelCase(trade, ' ');

export const SimpleContractorPicker = ({
  assignedContractorId,
  baselineAssignedContractorId,
  backgroundColor = 'inherit',
  disabled = false,
  size = 'medium',
  trade,
  filterByTrade = true,
  labelOverride,
  updateAssignedContractorId,
  showDefaultContractor = true,
}: {
  assignedContractorId?: string | null;
  baselineAssignedContractorId?: string | null;
  backgroundColor?: string;
  disabled?: boolean;
  size?: LabeledInputSize;
  trade: string;
  filterByTrade?: boolean;
  labelOverride?: string;
  showDefaultContractor?: boolean;
  updateAssignedContractorId: (contractorId: string | null) => void; // null means to reset to default
}) => {
  const { allOrgSettings } = useAllOrgSettings();
  const { contractors = [], contractorsMap = {}, defaultContractorsByTrade, baselineDefaultContractorsByTrade, showScanChanges } = useScanEditingContextSlice([
    'contractors',
    'contractorsMap',
    'defaultContractorsByTrade',
    'baselineDefaultContractorsByTrade',
    'showScanChanges',
  ]);
  const defaultContractorId = defaultContractorsByTrade?.[trade] ?? defaultContractorsByTrade?.general;
  const defaultContractor = defaultContractorId ? contractorsMap[defaultContractorId] : undefined;
  const setContractor = (contractorId: string | null) => {
    updateAssignedContractorId(contractorId);
  };
  const tradeDisplayName = labelOverride
    || getTradeDisplayName(trade, allOrgSettings?.tradeSettings?.trades);

  const contractorItems = useMemo(() => [
    defaultContractor && showDefaultContractor
      ? {
        value: '', // leave blank so it updates if the default contractor changes
        label: defaultContractor.name + ' (Default)',
        displayLabel: defaultContractor.name + ' (Default)',
      }
      : {
        value: '',
        label: 'No contractor assigned',
        displayLabel: 'No contractor assigned',
      },
    ...contractors
      .sort(contractorSort)
      .filter(contractor => !filterByTrade || contractorCanPerformTrade(contractor.trades, trade))
      .map(contractor => ({
        value: contractor.id,
        label: `${contractor.name} (${
          contractor.trades?.map(contractorTrade => getTradeDisplayName(contractorTrade, allOrgSettings?.tradeSettings?.trades)).join(', ')
        })`,
        displayLabel: contractor.name,
      })),
  ], [allOrgSettings?.tradeSettings?.trades, contractors, trade, showDefaultContractor, defaultContractor, filterByTrade]);

  const signalChange = showScanChanges
    && contractorHasChanged(trade, assignedContractorId, baselineAssignedContractorId, defaultContractorsByTrade, baselineDefaultContractorsByTrade);

  return (
    <div css={CONTRACTOR_PICKER_STYLE} style={{ backgroundColor }}>
      <LabeledSelect
        size={size}
        label={tradeDisplayName}
        value={assignedContractorId || ''}
        defaultValue={defaultContractorId || ''}
        defaultDisplayValue={defaultContractorId ? contractorsMap[defaultContractorId]?.name : undefined}
        placeholder={`Select`}
        onChange={setContractor}
        disabled={disabled}
        items={contractorItems}
        backgroundColor={signalChange ? COLORS2.alwaysDark.lightYellow : backgroundColor ?? 'inherit'}
      />
    </div>
  );
};

export const DefaultContractorPicker = ({ scanRoot, updateDefaultContractorsByTrade, trade, orgMarketId, backgroundColor }: {
  scanRoot: ScanRootOptimized;
  updateDefaultContractorsByTrade: (trade: string, contractorId: string | null) => void;
  trade: string;
  orgMarketId: string | undefined;
  backgroundColor?: string;
}) => {
  return (
    <SimpleContractorPicker
      assignedContractorId={scanRoot.defaultContractorsByTrade?.[trade]}
      updateAssignedContractorId={(contractorId: string | null) => updateDefaultContractorsByTrade(trade, contractorId)}
      trade={trade}
      size='medium'
      showDefaultContractor={trade !== 'general'}
      backgroundColor={backgroundColor ?? 'var(--background)'}
    />
  );
};
